import {
  Code2,
  Server,
  Database,
  Cloud,
  Briefcase,
  LifeBuoy,
  PhoneCall,
  Send,
  Share2,
  Target,
  LineChart,
  BarChart3,
  Megaphone,
  type LucideIcon,
} from "lucide-react";

export type RoleCategory =
  | "engineering"
  | "executive"
  | "customer-ops"
  | "growth"
  | "analytics";

export interface RoleWorkflow {
  title: string;
  description: string;
}

export interface Role {
  code: string;
  category: RoleCategory;
  name: string;
  summary: string;
  description: string;
  capabilities: string[];
  tools: string[];
  startingPrice: string;
  workflows: RoleWorkflow[];
  icon: LucideIcon;
}

export const CATEGORIES: { id: RoleCategory; label: string; blurb: string }[] = [
  {
    id: "engineering",
    label: "Engineering",
    blurb: "Virtual engineers that ship code, review PRs, and keep systems running.",
  },
  {
    id: "executive",
    label: "Executive",
    blurb: "Executive assistants that tame your inbox and keep your calendar honest.",
  },
  {
    id: "customer-ops",
    label: "Customer Ops",
    blurb: "Responders that triage tickets, draft replies, and answer calls.",
  },
  {
    id: "growth",
    label: "Growth",
    blurb: "SDRs, campaign managers, and social managers that keep the funnel moving.",
  },
  {
    id: "analytics",
    label: "Analytics",
    blurb: "Analysts and BI developers that turn raw data into actionable insight.",
  },
];

export const ROLES: Role[] = [
  // ENGINEERING
  {
    code: "engineer-frontend",
    category: "engineering",
    name: "Frontend Engineer",
    summary: "Ships UI from tickets. Opens PRs with tests.",
    description:
      "A virtual frontend engineer that takes a ticket, clones your repo, implements the change, writes component tests, and opens a pull request for your team to review.",
    capabilities: [
      "React, Vue, Svelte, and vanilla TypeScript projects",
      "Component authoring with accessibility checks",
      "Visual regression tests via Playwright or Storybook",
      "Opens PRs with linked tickets and clean commit messages",
      "Responds to code review feedback in the PR thread",
    ],
    tools: ["GitHub", "Linear", "Jira", "Figma", "Slack"],
    startingPrice: "$99/mo",
    workflows: [
      {
        title: "Bug ticket to merged PR",
        description:
          "Ticket lands in Linear. Agent reads the repro, writes a fix, adds a regression test, opens the PR with a reviewer assigned.",
      },
      {
        title: "Design handoff",
        description:
          "A Figma frame is attached to a ticket. Agent scaffolds the component, wires props, and ships a story in Storybook.",
      },
    ],
    icon: Code2,
  },
  {
    code: "engineer-backend",
    category: "engineering",
    name: "Backend Engineer",
    summary: "Builds APIs. Ships safe, tested Go and Node.",
    description:
      "A virtual backend engineer that implements endpoints, writes integration tests, and keeps API contracts clean. Comes with a built-in understanding of Go, Node, and Python.",
    capabilities: [
      "Designs REST and gRPC endpoints from spec",
      "Writes integration tests against a local database",
      "Adds migrations when the schema changes",
      "Follows existing repository and handler patterns",
      "Respects tenant scoping and authentication contracts",
    ],
    tools: ["GitHub", "Linear", "Postgres", "Cloud Run", "Slack"],
    startingPrice: "$149/mo",
    workflows: [
      {
        title: "New endpoint delivery",
        description:
          "Spec lands in Linear. Agent writes the handler, repository method, migration, and tests, then opens a PR.",
      },
      {
        title: "Bug fix with reproduction",
        description:
          "Agent reproduces the bug locally via an added test, fixes the code, and confirms the test now passes in CI.",
      },
    ],
    icon: Server,
  },
  {
    code: "engineer-database",
    category: "engineering",
    name: "Database Engineer",
    summary: "Proposes safe migrations. Reviews query plans.",
    description:
      "A virtual database engineer that writes forward-only migrations, reviews query plans for expensive scans, and flags indexes that need attention.",
    capabilities: [
      "Forward-only migration scripts with rollback notes",
      "EXPLAIN ANALYZE review on slow queries",
      "Index recommendations backed by pg_stat data",
      "Schema change PRs gated behind a human approval",
      "Postgres, MySQL, BigQuery, and Snowflake familiarity",
    ],
    tools: ["Postgres", "BigQuery", "GitHub", "DataDog", "Slack"],
    startingPrice: "$149/mo",
    workflows: [
      {
        title: "Slow query triage",
        description:
          "Agent pulls the worst queries from pg_stat, proposes an index, and opens a migration PR with before and after plans.",
      },
      {
        title: "Schema change review",
        description:
          "Agent reviews a teammate's migration for tenant scoping, nullability, and backfill safety before merge.",
      },
    ],
    icon: Database,
  },
  {
    code: "engineer-infrastructure",
    category: "engineering",
    name: "Infrastructure Engineer",
    summary: "Builds pipelines. Owns Terraform and CI.",
    description:
      "A virtual infrastructure engineer that maintains Terraform modules, CI pipelines, and deployment scripts. Keeps the plumbing boring in the best way.",
    capabilities: [
      "Terraform modules for GCP, AWS, and Cloudflare",
      "GitHub Actions workflows for build, test, deploy",
      "Cloud Run, Cloud Functions, and Kubernetes manifests",
      "Observability wiring via OpenTelemetry",
      "Cost review against your cloud bill",
    ],
    tools: ["Terraform", "GitHub Actions", "GCP", "AWS", "Slack"],
    startingPrice: "$199/mo",
    workflows: [
      {
        title: "New service bootstrap",
        description:
          "Agent scaffolds a Terraform module, CI workflow, and Cloud Run deploy script for a new Go service.",
      },
      {
        title: "Cost review",
        description:
          "Agent summarizes last month's cloud bill, flags top five cost drivers, and proposes three concrete reductions.",
      },
    ],
    icon: Cloud,
  },
  // EXECUTIVE
  {
    code: "executive-assistant",
    category: "executive",
    name: "Executive Assistant",
    summary: "Inbox triage, calendar, and booking coordination.",
    description:
      "A virtual executive assistant that triages your inbox, drafts replies in your voice, books meetings across time zones, and surfaces what actually needs your attention.",
    capabilities: [
      "Inbox triage with a prioritized daily brief",
      "Drafted replies in your tone, with factual citations",
      "Meeting scheduling across Gmail or Outlook calendars",
      "Travel coordination with flights, hotels, and ground",
      "Meeting prep docs with attendee context",
    ],
    tools: ["Gmail", "Outlook", "Google Calendar", "Zoom", "Slack"],
    startingPrice: "$199/mo",
    workflows: [
      {
        title: "Morning inbox brief",
        description:
          "At 7am your EA delivers a two-minute brief: what to reply to, what to skip, what to delegate, with drafts ready.",
      },
      {
        title: "Meeting booking",
        description:
          "A partner asks for time. EA finds a slot, sends an invite, and posts the meeting context into Slack.",
      },
    ],
    icon: Briefcase,
  },
  // CUSTOMER OPS
  {
    code: "ticket-responder",
    category: "customer-ops",
    name: "Ticket Responder",
    summary: "Triages tickets and drafts first replies.",
    description:
      "A virtual customer-support responder that reads incoming tickets, classifies them, looks up context, and drafts a first reply that your human agents approve with one click.",
    capabilities: [
      "Ticket classification by product area and urgency",
      "Context lookup across your knowledge base",
      "Drafted replies with citation links to source docs",
      "Auto-reply for high-confidence cases, human for the rest",
      "SLA tracking with escalation on breach",
    ],
    tools: ["Zendesk", "Intercom", "VirtualDesk Tickets", "Slack", "Notion"],
    startingPrice: "$99/mo",
    workflows: [
      {
        title: "First reply in under five minutes",
        description:
          "Ticket arrives. Agent classifies, drafts a reply with doc links, and sends if confidence is high.",
      },
      {
        title: "Escalation routing",
        description:
          "On a refund request or technical regression the agent escalates to the right human with full context attached.",
      },
    ],
    icon: LifeBuoy,
  },
  {
    code: "call-center-agent",
    category: "customer-ops",
    name: "Call Center Agent",
    summary: "Answers phones 24/7. Transcribes every call.",
    description:
      "A voice-native customer-support agent that answers inbound calls, handles routine requests, and escalates the rest to a human with full transcript and summary.",
    capabilities: [
      "Inbound call answering with natural voice",
      "Real-time transcription and post-call summary",
      "Escalation to human with hot handoff",
      "Appointment booking by voice",
      "Multi-language support (English, Spanish, French)",
    ],
    tools: ["Twilio", "VirtualDesk Tickets", "Google Calendar", "Slack"],
    startingPrice: "$299/mo",
    workflows: [
      {
        title: "After-hours coverage",
        description:
          "Customer calls at 2am. Agent answers, handles the FAQ, opens a ticket, and emails the human team a summary.",
      },
      {
        title: "Appointment booking",
        description:
          "Caller asks for a plumber. Agent finds the next open slot, confirms address, books it, and texts confirmation.",
      },
    ],
    icon: PhoneCall,
  },
  // GROWTH
  {
    code: "sdr",
    category: "growth",
    name: "Sales Development Rep",
    summary: "Finds leads. Writes first-touch outreach.",
    description:
      "A virtual SDR that researches target accounts, finds the right buyer, writes personalized first-touch outreach, and books discovery calls into your calendar.",
    capabilities: [
      "Target account research with firmographic filters",
      "Buyer identification from LinkedIn and public sources",
      "Personalized first-touch emails with clear CTAs",
      "Follow-up cadence with respectful pacing",
      "Discovery call booking into your team's calendar",
    ],
    tools: ["LinkedIn", "Apollo", "HubSpot", "Gmail", "Google Calendar"],
    startingPrice: "$249/mo",
    workflows: [
      {
        title: "Warm outbound campaign",
        description:
          "Agent loads a list of 200 target accounts, researches each, and sends 50 personalized emails per day.",
      },
      {
        title: "Discovery booking",
        description:
          "Reply lands. Agent proposes three time slots, sends the invite, and posts the meeting to Slack.",
      },
    ],
    icon: Send,
  },
  {
    code: "social-media-manager",
    category: "growth",
    name: "Social Media Manager",
    summary: "Runs your channels. Writes, schedules, replies.",
    description:
      "A virtual social media manager that drafts posts in your voice, schedules them across channels, and replies to comments with your team's guardrails.",
    capabilities: [
      "Content calendar with weekly themes",
      "Drafted posts for X, LinkedIn, Instagram, and Bluesky",
      "Image prompt-to-asset via your art direction",
      "Engagement replies with escalation on sensitive topics",
      "Weekly performance report with next-week recommendations",
    ],
    tools: ["X", "LinkedIn", "Instagram", "Buffer", "Slack"],
    startingPrice: "$199/mo",
    workflows: [
      {
        title: "Weekly content cycle",
        description:
          "Monday the agent drafts five posts for the week, you approve, it schedules and replies to comments all week.",
      },
      {
        title: "Launch announcement",
        description:
          "New feature ships. Agent writes the announcement, generates assets, and schedules across all channels.",
      },
    ],
    icon: Share2,
  },
  {
    code: "ad-campaign-manager",
    category: "growth",
    name: "Ad Campaign Manager",
    summary: "Optimizes spend. Writes creatives. Reads the dashboard.",
    description:
      "A virtual ad-ops manager that launches and optimizes paid campaigns across Google, Meta, and LinkedIn, with weekly creative refreshes and clear ROAS reporting.",
    capabilities: [
      "Campaign setup with audience, creative, and budget",
      "A/B creative testing with fast iteration",
      "Daily spend monitoring with alert thresholds",
      "Weekly ROAS report with recommendations",
      "Attribution stitching across channels",
    ],
    tools: ["Google Ads", "Meta Ads", "LinkedIn Ads", "GA4", "Slack"],
    startingPrice: "$299/mo",
    workflows: [
      {
        title: "New product launch",
        description:
          "Agent stands up a three-channel campaign, writes four creative variants per channel, and sets pacing.",
      },
      {
        title: "Weekly optimization",
        description:
          "Each Monday the agent pauses losers, reinvests in winners, and proposes two new creative tests.",
      },
    ],
    icon: Target,
  },
  // ANALYTICS
  {
    code: "data-analyst",
    category: "analytics",
    name: "Data Analyst",
    summary: "Turns data into insight. Writes SQL, not just reports.",
    description:
      "A virtual data analyst that answers ad-hoc questions with well-documented SQL, turns the answers into charts, and keeps a living knowledge base of what was asked and why.",
    capabilities: [
      "Ad-hoc SQL on Postgres, BigQuery, and Snowflake",
      "Dashboards in Metabase, Looker, or Superset",
      "Documented metric definitions in a shared glossary",
      "Cohort and retention analyses on request",
      "Weekly digest of top five changes in your data",
    ],
    tools: ["BigQuery", "Snowflake", "Metabase", "Looker", "Slack"],
    startingPrice: "$199/mo",
    workflows: [
      {
        title: "Weekly metric review",
        description:
          "Agent ships a one-page Monday digest: what moved, why, and what to watch next week.",
      },
      {
        title: "Ad-hoc question",
        description:
          "You ask about churn by plan. Agent writes the SQL, ships a chart, and adds the metric to the glossary.",
      },
    ],
    icon: LineChart,
  },
  {
    code: "bi-developer",
    category: "analytics",
    name: "BI Developer",
    summary: "Builds the dashboards your team actually uses.",
    description:
      "A virtual BI developer that models your data, builds stable dashboards in the tool you already have, and keeps definitions consistent across reports.",
    capabilities: [
      "dbt model authoring with tests and docs",
      "Dashboard builds in Looker, Metabase, or Power BI",
      "Source-of-truth metric layer with versioned definitions",
      "Data-quality monitors with Slack alerts",
      "Stakeholder training sessions on the dashboards",
    ],
    tools: ["dbt", "Looker", "Metabase", "Power BI", "GitHub"],
    startingPrice: "$249/mo",
    workflows: [
      {
        title: "New product area dashboard",
        description:
          "Agent models the tables, writes dbt tests, builds the dashboard, and schedules the stakeholder walkthrough.",
      },
      {
        title: "Metric consolidation",
        description:
          "Agent reconciles three definitions of active user across teams into one canonical metric with docs.",
      },
    ],
    icon: BarChart3,
  },
  {
    code: "growth-marketer",
    category: "growth",
    name: "Growth Marketer",
    summary: "Writes, tests, and ships copy that converts.",
    description:
      "A virtual growth marketer that owns landing page copy, email sequences, and lifecycle campaigns, with experiments as the default mode.",
    capabilities: [
      "Landing page copy with three headline variants",
      "Lifecycle email sequences across onboarding, activation, revival",
      "A/B test setup and readout",
      "Blog post drafts with SEO metadata",
      "Monthly experiment log",
    ],
    tools: ["Webflow", "Customer.io", "HubSpot", "GA4", "Notion"],
    startingPrice: "$199/mo",
    workflows: [
      {
        title: "Landing page launch",
        description:
          "Agent writes three headline variants, three hero copies, and ships the A/B test with a readout plan.",
      },
      {
        title: "Onboarding sequence refresh",
        description:
          "Agent rewrites the new-user email sequence, splits into three variants, and measures day-7 activation.",
      },
    ],
    icon: Megaphone,
  },
];

export function findRole(code: string): Role | undefined {
  return ROLES.find((r) => r.code === code);
}

export function rolesByCategory(cat: RoleCategory): Role[] {
  return ROLES.filter((r) => r.category === cat);
}
